<template>
    <div id="contents" class="contents spirit-of-japan">
        <section class="main">
            <div class="texts">
                <div class="inner">
                    <p class="sub">A Journey to the True Spirit of Japan</p>
                    <h2>Encounter Festivals<br>
                        Embodying the Japanese Spirit</h2>
                    <p class="lead">
                        Japanese festivals are not merely for show.<br>
                        <br>
                        Festivals dating back several centuries have been carefully cherished as invaluable Japanese heritage;
                        regarded as manifestations of cultures unique to specific regions, displays of elaborate craftsmanship, or symbols of an unseen dialogue with sacred beings.<br>
                        <br>
                        This section introduces festivals which truly convey the depth of the Japanese spirit.
                    </p>
                </div>
            </div>
            <div class="img"><img src="../../assets/spirit_of_japan5/main01.webp" alt="" class="_pc"><img src="../../assets/spirit_of_japan5/main01_sp.webp" alt="" class="_sp"></div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan5/img01.webp" alt=""></div>
                <div class="txt">
                    <h3>The Heron Dance—A 150-Year-Old Drawing Comes to Life</h3>
                    <p>
                        Nestled in the heart of Shimane Prefecture, the town of Tsuwano is fondly known as the "Little Kyoto of San-in" for its historic townscape and charming atmosphere. Not only does it resemble Kyoto in that it is a basin terrain surrounded by mountains, but also because it ardently incorporates Kyoto’s cultural elements dating back to the Edo period. Taikodani Inari Shrine sits serenely on a high hilltop. To reach it, visitors first pass through a magnificent corridor of almost 1000 brilliant-red torii gates; a sight reminiscent of Kyoto’s iconic Fushimi Inari. In addition, every year in July, the town presents “Sagimai,” or the “Heron Dance,” as a ceremony to protect the people from illness and disease. This dance is a tradition kept alive in Tsuwano after originating at Kyoto’s Gion Festival centuries ago. Two dancers don giant majestic wings and parade around the town mimicking the courtship movements of a male and female heron. Steeped in over 400 years of history, this traditional dance is a priceless testimony to the performing arts of ancient Japan.<br>
                        It has been preserved to this day solely through the efforts of the Tsuwano townspeople, who have cherished such traditions wholeheartedly. Tsuwano is also renowned for “The One Hundred Landscapes of Tsuwano,” a late 19th century guidebook that depicts famous natural and manmade sites, traditional arts, customs, and everyday scenes. If you walk the town armed with your very own copy of this guidebook, you can enjoy admiring the picturesque scenes of yesteryear portrayed in the book first-hand, preserved just as they were a century and a half ago.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'fc4fddf4-d2fb-4a78-b8a3-967c84f13ecf', page: 'about', url: 'ancient-guidebook-tsuwano-shimane'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan5/img02.webp" alt=""></div>
                <div class="txt">
                    <h3>Hanagasa Festival―A Fabulous Parade of 10,000 Dancers</h3>
                    <p>
                        Famous throughout the world for its Zao Snow Monsters, Yamagata prefecture is a snow-covered wonderland. However, in early summer, it also serves as Japan’s largest producer of cherries. Of particular note is Sato Nishiki, a cherry variety so highly acclaimed that it was dubbed “the red diamond” in countries throughout the world.
                        Every summer, Yamagata holds one of the Tohoku region’s Top 4 festivals known as the “Hanagasa Festival.” Extravagantly decorated ornate floats (dashi) are carried down the street accompanied by over 10,000 dancers, who dance to Hanagasa Ondo (Flower Hat Folk Song), the Japanese folk song made specifically for this festival. Their flawless harmony captivates spectators, who number around one million.<br>
                        The dancers wear hats adorned with vibrant safflowers. Long ago, this region prospered as a trading hub for safflowers as the key ingredient for premium dyes once considered more valuable than gold. The red color extracted from safflower petals was valued as a raw ingredient for dyes, lipstick, and other goods. In this way, safflowers brought great wealth to this land.<br>
                        The significance of this flower in helping Yamagata flourish can be appreciated not only at the Hanagasa Festival, but also by wandering through vivid safflower fields, savoring cuisine made with safflowers, and visiting a former wealthy merchant’s residence housing gorgeous treasures of yesteryear.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'b4efb553-1155-4ddc-b5ab-195a00a06166', page: 'about', url:'mountain-temple\'s-safflower-culture-yamagata-cherries'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan5/img03.webp" alt=""></div>
                <div class="txt">
                    <h3>Encounter Fearsome Yet Kind Oni（Ogres） at Shujo Onie</h3>
                    <p>
                        Kunisaki Peninsula lies in the north-east of Oita prefecture and is home to a unique spiritual culture known as “Rokugo Manzan,” which dates back over 1300 years. This is the cradle of “Shibutsu Shugo,” a fusion of Japan’s ancient, nature-revering Shinto religion and Buddhism, which spread to Japan from the continent of Asia. The ascetic practices of monks who traverse rugged mountain peaks to worship both Shinto gods and Buddhas, are still delicately preserved to this day. When winter descends upon this land, fearsome creatures known in Japan as "Oni" (ogres) clutching blazing torches appear at its many temples. While Oni are generally seen as terrifying, those of Kunisaki are believed to be monks who have completed rigorous ascetic training and are honored accordingly as “Buddha incarnate.” These Oni are considered benevolent beings who bring happiness to people. Also, at a festival with over a thousand-year history, “Shujo Onie,” these Oni drink Japanese sake and share laughs with the villagers.<br>
                        This festival, which ensures ancient rituals remain alive and well to this day, has been designated an Important Intangible Folk Cultural Property of Japan.<br>
                        The torches lighting up the dark of night, the fusion of Shinto and Buddhism, and this wondrous symbiotic world woven by human connection reveal the profound depths of Japan's spiritual culture to visitors from far and wide.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'ccc2d69d-df80-4012-95e2-4ee07bb68d30', page: 'about', url: 'kunisaki-peninsula-where-ogres-bring-happiness-oita'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan5/img04.webp" alt=""></div>
                <div class="txt">
                    <h3>Johana Hikiyama-A Festival Packed With Bold Yet Delicate Woodcarving Techniques</h3>
                    <p>
                        Each year in May, the historic town of Johana in Nanto City, Toyama Prefecture, hosts the Johana Hikiyama Festival. Boasting over 300 years of history, this festival is a magnificent spectacle recognized as a UNESCO Intangible Cultural Asset. The main attraction of this festival is undisputedly the six meticulously-carved Hikiyama and Iori-Yatai floats. Skilled bearers carrying sacred polearms called “Kenboko,” and “Shishimai” (Lion Dance) lead the procession, performing a ritual to ward off malevolent spirits and purify the route for the deities represented by each float. Particularly noteworthy on these Hikiyama floats are the intricate Inami Carvings adorning them. Passed down from the great masters of 18th century Kyoto, this unique technique has been kept alive by the Inami Woodcarvers, one of the most elite groups of artisans in Japan responsible for restoring the nation’s important temples and shrines. At the museum of Johana Hikiyama Kaikan, the Hikiyama and Iori-Yatai floats are on display all year round. We invite you to come and witness firsthand the divine craftsmanship of the Inami Woodcarvers, which adds such vibrant color to the Johana Hikiyama Festival.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'f49814e3-acd7-4413-a284-1b4a040d1a1c', page: 'about', url: 'inami-born-chisels-master-woodcarvers-toyama-nanto-zuisenji'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan5/img05.webp" alt=""></div>
                <div class="txt">
                    <h3>Mukonage & Suminuri-A Curious Festival Unique to Snow Country</h3>
                    <p>
                        Tucked away in one of the world’s heaviest snowfall regions, Tokamachi is a city in Niigata Prefecture offering an extremely unique festival known as the “Mukonage and Suminuri” Festival. Dating back over three centuries, this vibrant event is held every January 15th on Lunar New Year at Matsunoyama Onsen. Mukonage is a tradition where newlywed grooms are thrown from the grounds of Kannon-hall down a snowy slope of approximately 5 meters. Originally performed as a way to even the score with the man who had taken away one of the village daughters through marriage, this festivity gradually transformed into a joyous celebration aimed at deepening the bond between husband and wife. Today, mukonage is so popular that couples from throughout Japan travel to Matsunoyama Onsen to take part in this celebration. After the groom-tossing, the ritual of suminuri (ash smearing) is performed to pray for good health and family prosperity. A sacred bonfire known as Sai no Kami is lit and left to burn to the ground. The remaining ashes are mixed with snow, then the husband and wife smear the now pitch-black snow on each other’s faces while shouting “omedeto!” (congratulations). In addition to this festival, Tokamachi offers visitors a unique chance to immerse themselves in authentic northern Japanese culture, featuring exquisite textile traditions such as Tokamachi Yuzen, culinary heritage, and the Snow Festival showcasing intricate snow sculptures crafted by local residents.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'c8f73973-e75e-4567-a512-5c10e0173744', page: 'about', url: 'tokamachi-ultimate-snow-country-niigata'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
    </section>
    <section class="section03">
            <div class="inner">
                <h2>Other Articles</h2>
                <ul>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan1'}">
                            <div class="img"><img src="../../assets/spirit_of_japan/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Discover Craftsmanship Unbowed by Time</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan2'}">
                            <div class="img"><img src="../../assets/spirit_of_japan2/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Sacred Sites where Nature and Faith Blend into One</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan3'}">
                            <div class="img"><img src="../../assets/spirit_of_japan3/other.png" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Unlocking the Secrets of the Shogun, Samurai and Ninja</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan4'}">
                            <div class="img"><img src="../../assets/spirit_of_japan4/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">The Journey of Japan's
                                    Original Landscapes Shaped by Water</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan6'}">
                            <div class="img"><img src="../../assets/spirit_of_japan6/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Ancient Paths
                                    Where Japanese Culture Dwells</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan7'}">
                            <div class="img"><img src="../../assets/spirit_of_japan7/main01.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">“O-sake” — the drink that nurtured Japanese history</div>
                            </div>
                        </router-link>
                    </li>
                </ul>
            </div>
        </section>
    </div>
</template>

<script>
import ApiRouter from "../../components/Systems/ApiRouter";

export default {
    name: "PlanYourVisit",
    mixins: [ApiRouter],
    data: function () {
        return {
            articles: [],
            activeTabMenu: 'half'
        }
    },
    created: function () {
        this.loadData();
        this.getQuery();
        document.title = 'Spirit Of Japan | Japan Heritage Official Site'
    },
    methods: {
        loadData: function () {
            window.axios.get(this.routes.articles)
                .then((response) => {
                    this.articles = response.data.data;
                });
        },
        getQuery: function () {
            const tab = this.$route.query.tab;

            switch (tab) {
                case 'day1':
                    this.activeTabMenu = 'day1';
                    break;
                case 'day2':
                    this.activeTabMenu = 'day2';
                    break;
                case 'day3':
                    this.activeTabMenu = 'day3';
                    break;
                default:
                    this.activeTabMenu = 'half';
                    break;
            }
        },
        articleCardHasClicked: function (id, hash, breadcrumb) {
            this.$router.push({name: 'Popular', params: {id: id, page: 'travelInfo', breadcrumb: breadcrumb}, hash: hash});
        }
    },
    watch: {
        $route() {
            this.getQuery();
        }
    }
}
</script>

<style src="./SpiritOfJapan5.scss" lang="scss" scoped />
